import React from "react"
import { LayoutPageContent } from "components"
import {
  RouteComponentProps,
  StaticContext,
  useLocation,
  useRouteMatch,
  withRouter
} from "react-router"
import { useHistory, Route, Switch, Link } from "react-router-dom"
import { useConfigurationContext } from "contexts/configuration/context"
import { Button, Layout, Tabs } from "antd"
import { PlusOutlined } from "@ant-design/icons"
import {
  useOpusPermissions,
  EOpusPermission
} from "contexts/authorization/hooks/use-opus-permissions"

import { Summary } from "./sub-views/summary"
import { Policies } from "./sub-views/policies"
import { Quotes } from "./sub-views/quotes"
import { Header } from "./header"
import { useQuery } from "react-query"
import usePlatform from "contexts/platform/use-platform"
import { AxiosError } from "axios"
import { TErrorState } from "models/errors"
import { useDefaultPage } from "utils/hooks/use-default-page"

const ClientWithBaseRedirect: React.FC<ClientProps> = (props) => {
  const history = useHistory()
  if (!props.match.params.subpage) {
    history.replace(`/client/${props.match.params.id}/summary`)
    return <></>
  }

  return <Client {...props} />
}

const Client: React.FC<ClientProps> = (props) => {
  const history = useHistory()
  const location = useLocation()

  const { path } = useRouteMatch()
  const configuration = useConfigurationContext()
  const [apiError, setApiError] = React.useState<TErrorState>()

  useDefaultPage(
    apiError?.errorNumber ? Number(apiError.errorNumber) : undefined,
    ["404", "400"],
    {
      status: "404",
      title: `Sorry, we couldn't find a client that matched those details.`,
      buttonText: "Back to Client list",
      redirectTo: "/clients"
    }
  )

  const platform = usePlatform()
  const { havePermission: canManageBrokerQuotes } = useOpusPermissions(
    EOpusPermission.BrokerQuoteManagement,
    "true"
  )

  const getPolicyholder = useQuery(
    ["getPolicyholder", props.match.params.id],
    () => platform.policyholders.getPolicyholder(props.match.params.id),
    {
      retry: (failureCount, error: AxiosError) => {
        const status = error.response?.status
        if (status && status >= 400 && status < 500) return false
        return failureCount < 3
      },
      onError: (error: AxiosError) => {
        setApiError({
          errorNumber: error.response?.status ?? 500,
          validationMessage: "Unable to load client"
        })
      }
    }
  )

  const getDisplayName = () => {
    const client = getPolicyholder.data
    if (!client) return undefined

    if (client.legalName) return client.legalName
    if (client.businessName) return client.businessName
    if (client.contact?.name?.firstName || client.contact?.name?.lastName) {
      return `${client.contact.name.firstName || ""} ${
        client.contact.name.lastName || ""
      }`.trim()
    }
    return client.referenceID || undefined
  }

  return (
    <Layout>
      <Header
        configuration={configuration}
        title={getDisplayName()}
        breadcrumb={getPolicyholder.data?.referenceID}
        isWorking={getPolicyholder.isLoading}
        actions={
          getPolicyholder.data && (
            <>
              {canManageBrokerQuotes && (
                <Link
                  to={`/client-quote/create/${props.match.params.id}`}
                  className="ant-btn ant-btn-primary"
                  style={{ marginRight: 8 }}
                >
                  <PlusOutlined /> New Quote
                </Link>
              )}
              <Button
                onClick={() =>
                  history.push(`/clients/edit/${props.match.params.id}`)
                }
              >
                Edit Client
              </Button>
            </>
          )
        }
        tabs={
          <Tabs
            activeKey={props.match.params.subpage}
            onTabClick={(key) => {
              const url = `/client/${props.match.params.id}/${key}`
              if (location.pathname === url) history.replace(url)
              else history.push(url)
            }}
          >
            <Tabs.TabPane tab="Summary" key="summary" />
            <Tabs.TabPane tab="Policies" key="policies" />
            <Tabs.TabPane tab="Quotes" key="quotes" />
          </Tabs>
        }
      ></Header>
      <LayoutPageContent transparent>
        <Switch>
          <Route
            path={`${path}/summary`}
            render={() => (
              <Summary
                client={getPolicyholder.data}
                isLoading={getPolicyholder.isFetching}
              />
            )}
          />
          <Route
            path={`${path}/policies`}
            render={() => <Policies clientID={props.match.params.id} />}
          />
          <Route
            path={`${path}/quotes`}
            render={() => <Quotes clientID={props.match.params.id} />}
          />
        </Switch>
      </LayoutPageContent>
    </Layout>
  )
}

type ClientProps = RouteComponentProps<
  {
    id: string
    subpage: string
  },
  StaticContext,
  {
    data?: string
  }
>

export default withRouter(ClientWithBaseRedirect)
